/**
 * Security Initialization Module
 *
 * Centralizes all security-related initialization and configuration
 * for the AdapTeach application.
 */

import {
  initializeCSRFProtection,
  clearCSRFToken,
  generateCSRFToken,
} from "./csrfProtection";
import {
  initializeRateLimiting,
  cleanupRateLimitData,
  cleanupRateLimiting,
} from "./rateLimiting";
import { validateContentSecurity } from "./sanitization";
import { supabase } from "../lib/supabase";

// Extend Window interface for security flags
declare global {
  interface Window {
    __adapteach_auth_listener_initialized?: boolean;
    __adapteach_security_initialized?: boolean;
  }
}

// Global cleanup interval references
let securityCleanupInterval: NodeJS.Timeout | null = null;
let devToolsDetectionInterval: NodeJS.Timeout | null = null;

// Global security initialization promise to prevent multiple concurrent initializations
let securityInitPromise: Promise<void> | null = null;

// Auto-initialize security when module loads (independent of React)
const autoInitializeSecurity = async () => {
  // Only auto-initialize in browser environment
  if (typeof window === "undefined") return;

  // Wait a short moment for the DOM to be ready and avoid race conditions
  await new Promise(resolve => setTimeout(resolve, 200));

  try {
    await initializeSecurity({
      enableCSRF: true,
      enableRateLimiting: true,
      enableContentValidation: true,
      enableSecurityLogging: true,
      developmentMode: process.env.NODE_ENV === "development",
    });
  } catch (error) {
    console.error("❌ Auto-initialization of security failed:", error);
  }
};

// Start auto-initialization immediately when module loads
if (typeof window !== "undefined") {
  autoInitializeSecurity();
}

/**
 * Security configuration interface
 */
export interface SecurityConfig {
  enableCSRF: boolean;
  enableRateLimiting: boolean;
  enableContentValidation: boolean;
  enableSecurityLogging: boolean;
  developmentMode: boolean;
}

/**
 * Default security configuration
 */
const DEFAULT_SECURITY_CONFIG: SecurityConfig = {
  enableCSRF: true,
  enableRateLimiting: true,
  enableContentValidation: true,
  enableSecurityLogging: process.env.NODE_ENV === "production",
  developmentMode: process.env.NODE_ENV === "development",
};

/**
 * Security event types for logging
 */
export type SecurityEventType =
  | "csrf_validation_failed"
  | "rate_limit_exceeded"
  | "xss_attempt_detected"
  | "invalid_input_detected"
  | "auth_failure"
  | "suspicious_activity";

/**
 * Security event interface
 */
export interface SecurityEvent {
  type: SecurityEventType;
  message: string;
  userId?: string;
  userAgent?: string;
  ip?: string;
  timestamp: number;
  severity: "low" | "medium" | "high" | "critical";
  metadata?: Record<string, unknown>;
}

/**
 * Security logger class
 */
class SecurityLogger {
  private events: SecurityEvent[] = [];
  private maxEvents = 1000;
  private config: SecurityConfig;

  constructor(config: SecurityConfig) {
    this.config = config;
  }

  /**
   * Log a security event
   */
  logEvent(event: Omit<SecurityEvent, "timestamp">): void {
    if (!this.config.enableSecurityLogging) return;

    const fullEvent: SecurityEvent = {
      ...event,
      timestamp: Date.now(),
    };

    this.events.push(fullEvent);

    // Keep only the most recent events
    if (this.events.length > this.maxEvents) {
      this.events = this.events.slice(-this.maxEvents);
    }

    // Log to console in development
    if (this.config.developmentMode) {
      const emoji = this.getSeverityEmoji(event.severity);
      console.warn(
        `${emoji} Security Event [${event.type}]: ${event.message}`,
        event.metadata
      );
    }

    // In production, you might want to send to a logging service
    if (!this.config.developmentMode && event.severity === "critical") {
      this.reportCriticalEvent(fullEvent);
    }
  }

  /**
   * Get emoji for severity level
   */
  private getSeverityEmoji(severity: SecurityEvent["severity"]): string {
    switch (severity) {
      case "low":
        return "🔵";
      case "medium":
        return "🟡";
      case "high":
        return "🟠";
      case "critical":
        return "🔴";
      default:
        return "⚪";
    }
  }

  /**
   * Report critical security events
   */
  private reportCriticalEvent(event: SecurityEvent): void {
    // In a real application, you would send this to your security monitoring service
    console.error("🚨 CRITICAL SECURITY EVENT:", event);
  }

  /**
   * Get recent security events
   */
  getEvents(limit?: number): SecurityEvent[] {
    const events = this.events.slice().reverse();
    return limit ? events.slice(0, limit) : events;
  }

  /**
   * Get events by type
   */
  getEventsByType(type: SecurityEventType): SecurityEvent[] {
    return this.events.filter(event => event.type === type);
  }

  /**
   * Clear old events
   */
  cleanup(): void {
    const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    this.events = this.events.filter(event => event.timestamp > oneWeekAgo);
  }
}

/**
 * Global security logger instance
 */
let securityLogger: SecurityLogger;

/**
 * Get the security logger instance
 */
export const getSecurityLogger = (): SecurityLogger => {
  if (!securityLogger) {
    securityLogger = new SecurityLogger(DEFAULT_SECURITY_CONFIG);
  }
  return securityLogger;
};

/**
 * Log a security event (convenience function)
 */
export const logSecurityEvent = (
  type: SecurityEventType,
  message: string,
  severity: SecurityEvent["severity"] = "medium",
  metadata?: Record<string, unknown>
): void => {
  const logger = getSecurityLogger();
  logger.logEvent({
    type,
    message,
    severity,
    metadata,
    userAgent: navigator.userAgent,
  });
};

/**
 * Content Security Policy violation handler
 */
const handleCSPViolation = (event: SecurityPolicyViolationEvent): void => {
  logSecurityEvent(
    "xss_attempt_detected",
    `CSP violation: ${event.violatedDirective}`,
    "high",
    {
      blockedURI: event.blockedURI,
      violatedDirective: event.violatedDirective,
      originalPolicy: event.originalPolicy,
      sourceFile: event.sourceFile,
      lineNumber: event.lineNumber,
    }
  );
};

/**
 * Set up security event listeners
 */
const setupSecurityEventListeners = (): void => {
  // CSP violation reporting
  document.addEventListener("securitypolicyviolation", handleCSPViolation);

  // Unhandled promise rejections (potential security issues)
  window.addEventListener("unhandledrejection", event => {
    logSecurityEvent(
      "suspicious_activity",
      `Unhandled promise rejection: ${event.reason}`,
      "medium",
      { reason: event.reason }
    );
  });

  // Console access detection (basic)
  if (
    typeof window !== "undefined" &&
    !DEFAULT_SECURITY_CONFIG.developmentMode
  ) {
    // Clear existing interval if any
    if (devToolsDetectionInterval) {
      clearInterval(devToolsDetectionInterval);
    }

    let devtools = false;
    devToolsDetectionInterval = setInterval(() => {
      if (
        window.outerHeight - window.innerHeight > 200 ||
        window.outerWidth - window.innerWidth > 200
      ) {
        if (!devtools) {
          devtools = true;
          logSecurityEvent(
            "suspicious_activity",
            "Developer tools opened",
            "low"
          );
        }
      } else {
        devtools = false;
      }
    }, 500);
  }
};

/**
 * Initialize all security measures
 */
export const initializeSecurity = async (
  config?: Partial<SecurityConfig>
): Promise<void> => {
  // If security is already initialized, return immediately
  if (
    typeof window !== "undefined" &&
    window.__adapteach_security_initialized
  ) {
    return;
  }

  // If initialization is already in progress, wait for it to complete
  if (securityInitPromise) {
    return securityInitPromise;
  }

  const finalConfig = { ...DEFAULT_SECURITY_CONFIG, ...config };

  // Create the initialization promise
  securityInitPromise = (async () => {
    try {
      // Mark security as initialized globally at the start
      if (typeof window !== "undefined") {
        window.__adapteach_security_initialized = true;
      }
      // Initialize CSRF protection
      if (finalConfig.enableCSRF) {
        await initializeCSRFProtection();
      }

      // Initialize rate limiting
      if (finalConfig.enableRateLimiting) {
        initializeRateLimiting();
      }

      // Set up security event listeners
      if (finalConfig.enableSecurityLogging) {
        setupSecurityEventListeners();
      }

      // Set up periodic cleanup
      if (securityCleanupInterval) {
        clearInterval(securityCleanupInterval);
      }
      securityCleanupInterval = setInterval(
        () => {
          cleanupRateLimitData();
          getSecurityLogger().cleanup();
        },
        60 * 60 * 1000
      ); // Every hour

      // Set up auth state change handler for security (only once)
      if (
        typeof window !== "undefined" &&
        !window.__adapteach_auth_listener_initialized
      ) {
        window.__adapteach_auth_listener_initialized = true;

        supabase.auth.onAuthStateChange(async (event, session) => {
          if (event === "SIGNED_OUT") {
            // Clear security tokens on logout
            clearCSRFToken();

            logSecurityEvent("auth_failure", "User signed out", "low", {
              userId: session?.user?.id,
            });
          } else if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
            // Regenerate CSRF token on auth state changes
            try {
              await generateCSRFToken();
            } catch (error) {
              console.error("❌ Failed to regenerate CSRF token:", error);
            }

            logSecurityEvent("auth_failure", "User signed in", "low", {
              userId: session?.user?.id,
            });
          }
        });
      }
    } catch (error) {
      console.error("❌ Security initialization failed:", error);

      // Reset the global flag on error so initialization can be retried
      if (typeof window !== "undefined") {
        window.__adapteach_security_initialized = false;
      }

      logSecurityEvent(
        "suspicious_activity",
        `Security initialization failed: ${error}`,
        "critical",
        { error: error instanceof Error ? error.message : String(error) }
      );

      throw error; // Re-throw to allow caller to handle
    } finally {
      // Clear the promise reference
      securityInitPromise = null;
    }
  })();

  return securityInitPromise;
};

/**
 * Cleanup security initialization (call on app shutdown)
 */
export const cleanupSecurity = (): void => {
  // Clear all intervals
  if (securityCleanupInterval) {
    clearInterval(securityCleanupInterval);
    securityCleanupInterval = null;
  }

  if (devToolsDetectionInterval) {
    clearInterval(devToolsDetectionInterval);
    devToolsDetectionInterval = null;
  }

  // Cleanup rate limiting
  cleanupRateLimiting();

  // Clear security tokens
  clearCSRFToken();

  // Reset security initialization flags and promise
  if (typeof window !== "undefined") {
    window.__adapteach_auth_listener_initialized = false;
    window.__adapteach_security_initialized = false;
  }
  securityInitPromise = null;
};

/**
 * Check if security is currently initialized
 */
export const isSecurityInitialized = (): boolean => {
  return (
    typeof window !== "undefined" &&
    window.__adapteach_security_initialized === true
  );
};

/**
 * Validate application security status
 */
export const validateSecurityStatus = (): {
  secure: boolean;
  issues: string[];
  recommendations: string[];
} => {
  const issues: string[] = [];
  const recommendations: string[] = [];

  // Check if running over HTTPS in production
  if (location.protocol !== "https:" && process.env.NODE_ENV === "production") {
    issues.push("Application not running over HTTPS");
    recommendations.push("Enable HTTPS for production deployment");
  }

  // Check for CSP header
  const metaCSP = document.querySelector(
    'meta[http-equiv="Content-Security-Policy"]'
  );
  if (!metaCSP) {
    issues.push("No Content Security Policy detected");
    recommendations.push("Implement Content Security Policy headers");
  }

  // Note: We can't check response headers from client-side JavaScript
  // This would need to be done server-side or through a security scanning tool

  return {
    secure: issues.length === 0,
    issues,
    recommendations,
  };
};

/**
 * Emergency security lockdown
 */
export const emergencyLockdown = (reason: string): void => {
  console.error("🚨 EMERGENCY SECURITY LOCKDOWN:", reason);

  logSecurityEvent(
    "suspicious_activity",
    `Emergency lockdown triggered: ${reason}`,
    "critical"
  );

  // Clear all sensitive data
  clearCSRFToken();

  // Clear localStorage and sessionStorage
  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch (error) {
    console.error("Failed to clear storage:", error);
  }

  // Sign out user
  supabase.auth.signOut();

  // Redirect to safe page
  window.location.href = "/";
};

/**
 * Security health check
 */
export const performSecurityHealthCheck = (): {
  healthy: boolean;
  checks: Record<string, boolean>;
  issues: string[];
} => {
  const checks: Record<string, boolean> = {};
  const issues: string[] = [];

  // Check CSRF token availability
  checks.csrfToken = sessionStorage.getItem("adapteach_csrf_token") !== null;
  if (!checks.csrfToken) {
    issues.push("CSRF token not found");
  }

  // Check rate limiting functionality
  checks.rateLimiting = typeof window !== "undefined";

  // Check content validation
  const testContent = '<script>alert("test")</script>';
  const validation = validateContentSecurity(testContent);
  checks.contentValidation =
    !validation.isSecure && validation.issues.length > 0;

  const healthy =
    Object.values(checks).every(check => check) && issues.length === 0;

  return {
    healthy,
    checks,
    issues,
  };
};
